import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import logo from "../../assets/images/rentokil-logo.svg";
import { LoginDivContainer, LoginImg } from "./LoginForm.style";
import { Typography } from "@mui/material";
import { ToastType, notifyToast, FormPage } from "../../components-old/ComponentsIndex";
import { AuthAPI, VantagePortalAPI } from "../../apis/api-index";
import SettingsModal from "../Settings/SettingsModal";
import { selectEmployee, selectSessionConfig, selectUser, selectUserRoles } from "../../store/store-selector";
import { clearUser, setEmployee, setUserRole, setSessionConfig } from "../../store/slice-index";

const LoginForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const employee = useSelector(selectEmployee);
  const userRole = useSelector(selectUserRoles);
  const sessionConfig = useSelector(selectSessionConfig);

  const location = useLocation();
  const businessSorceItemList = location.state;
  const spinnerCtx = useContext(DisplaySpinnerContext);

  const [selectedBusinessSourceValue, setSelectedBusinessSourceValue] = useState("");
  const [selectedAppLanguage, setSelectedAppLanguage] = useState("en_GB");

  const onBusinessSourceSelection = (event) => {
    setSelectedBusinessSourceValue(event.target.value);
  };

  const onLanguageSelection = (event) => {
    setSelectedAppLanguage(event.target.value);
  };

  const handleAppLanguage = async () => {
    try {
      await i18n.changeLanguage(selectedAppLanguage);
    } catch (err) {
      alert(err);
    }
  };

  const handleConfirm = async () => {
    try {
      const [businessCode, countryCode, locale] = selectedBusinessSourceValue.split("-").concat(selectedAppLanguage);
      if (businessCode && countryCode) {
        spinnerCtx.setLoadingIndicator(true);
        const empUserRoleResponse = await VantagePortalAPI.getUserRole(countryCode, businessCode);
        if (empUserRoleResponse && empUserRoleResponse.employees.length > 0) {
          await handleAppLanguage();
          dispatch(setEmployee(empUserRoleResponse.employees[0]));
          empUserRoleResponse.userroles && dispatch(setUserRole(empUserRoleResponse.userroles));
          dispatch(
            setSessionConfig({
              businessCode: businessCode,
              countryCode: countryCode,
              locale: locale,
            })
          );
          notifyToast("Successfully Logged In", ToastType.SUCCESS);
        }
        spinnerCtx.setLoadingIndicator(false);
      } else {
        alert("No Business source available");
      }
    } catch (err) {
      alert(err);
    }
  };

  const handleSignOut = async () => {
    try {
      await AuthAPI.logOut();
      dispatch(clearUser());
      navigate("/");
    } catch (err) {
      alert(err);
    }
  };

  useEffect(() => {
    const errorSignOut = async () => {
      try {
        await AuthAPI.logOut();
        dispatch(clearUser());
        navigate("/");
      } catch (err) {
        alert(err);
      }
    };

    if (!businessSorceItemList) errorSignOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (employee && userRole && sessionConfig) {
      navigate("/home");
    }
  }, [navigate, employee, userRole, sessionConfig]);

  const displayLoginDivContent = () => {
    return (
      <>
        {businessSorceItemList && businessSorceItemList.length > 0 && (
          <SettingsModal
            settings={{
              initialWidth: 280,
              imageUrl: user.userRecord ? user.userRecord.photoURL : "",
              modalTitle: t("generic.settings"),
              modalFirstActionDesc: t("generic.cancel"),
              modalSecondActionDesc: t("generic.confirm"),
              sourcePlaceholder: t("generic.businessSource"),
              sourceListLabel: t("generic.businessSource"),
              sourceItem: businessSorceItemList,
              languagePlaceholder: "Language",
              languageListLabel: "Language",
              selectedBusinessSourceValue: selectedBusinessSourceValue,
              selectedAppLanguage: selectedAppLanguage,
              onBusinessSourceSelection: onBusinessSourceSelection,
              onLanguageSelection: onLanguageSelection,
              onModalFirstActionClick: handleSignOut,
              onModalSecondActionClick: handleConfirm,
            }}
          />
        )}
      </>
    );
  };

  return (
    <FormPage isLoginFlow={true}>
      <LoginDivContainer
        divStyle={{
          divMinHeight: "100vh",
          divFontSize: {
            mobileS: "30",
            mobileL: "25",
            tablet: "40",
            laptopL: "20",
          },
        }}
      >
        <LoginImg src={logo} alt="logo" />
        <Typography variant="bannerTextDarkBg">{t("login.header")}</Typography>
        {displayLoginDivContent()}
      </LoginDivContainer>
    </FormPage>
  );
};

export default LoginForm;
